import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useUser } from '@clerk/clerk-react';
import { db } from "@/lib/supabase";
import { setAuditActor } from "@/lib/auditActor";
import type { Database } from "@/types/database.types";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";

export type Employee = Database["public"]["Tables"]["employees"]["Row"];

export interface UseLoggedInEmployeeResult {
  employee: Employee | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const LoggedInEmployeeContext = createContext<UseLoggedInEmployeeResult>({
  employee: null,
  loading: true,
  error: null,
  refresh: async () => { },
});

async function fetchEmployeeByEmail(email: string): Promise<{ employee: Employee | null; error: string | null }> {
  const { data, error } = await db.employees.getByEmail(email);
  if (error) return { employee: null, error: error.message ?? "Failed to load employee" };
  return { employee: data ?? null, error: null };
}

export function LoggedInEmployeeProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded } = useUser();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userEmailAddress = user?.primaryEmailAddress?.emailAddress ?? null;

  // `silent` refetches leave the `loading` flag untouched. This matters because
  // AccessGate unmounts the entire app while `loading` is true; a background
  // refetch (e.g. from a realtime employees change) must not tear down the tree.
  const performFetch = useCallback(async (silent: boolean) => {
    if (!isLoaded) {
      if (!silent) setLoading(true);
      return;
    }

    if (!userEmailAddress) {
      setEmployee(null);
      setError("No email address found for user");
      setLoading(false);
      return;
    }

    if (!silent) setLoading(true);
    const result = await fetchEmployeeByEmail(userEmailAddress);
    setEmployee(result.employee);
    setError(result.error);
    setLoading(false);
  }, [userEmailAddress, isLoaded]);

  const refresh = useCallback(() => performFetch(false), [performFetch]);
  const refreshSilently = useCallback(() => performFetch(true), [performFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Bridge the logged-in employee to the non-React audit actor store so
  // recordAudit() can attribute changes without access to React context.
  useEffect(() => {
    setAuditActor({
      id: employee?.id ?? null,
      email: employee?.email ?? userEmailAddress,
      name: employee?.name ?? null,
    });
  }, [employee, userEmailAddress]);

  // Refetch when the employees table changes remotely so role/office/etc. updates for the
  // currently signed-in user propagate without a manual reload. The fetch already filters
  // by email, so we accept the over-broad subscription rather than wiring a per-row filter.
  // Crucially this refetch is silent: it must not flip `loading` (AccessGate would otherwise
  // tear down and remount the whole app on every employee edit). Our own edits are skipped
  // entirely, since they don't change the signed-in user's own record in a way we haven't
  // already applied locally.
  useRealtimeRefresh(["employees"], refreshSilently, { skipRefetchOnSelfEdit: true });

  const value = useMemo<UseLoggedInEmployeeResult>(() => {
    return { employee, loading, error, refresh };
  }, [employee, loading, error, refresh]);

  return <LoggedInEmployeeContext.Provider value={value}>{children}</LoggedInEmployeeContext.Provider>;
}

export function useLoggedInEmployee(): UseLoggedInEmployeeResult {
  return useContext(LoggedInEmployeeContext);
}
